'use client';

import { useSession, signIn, signOut } from "next-auth/react"
import { useState, useEffect } from 'react';
import DeployedProjectTable from "@/components/ui/DeployedProjectTable";
import { Toaster } from "@/components/ui/toaster";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import CodeBox from '@/components/ui/codebox';
import GitHubInstallPrompt from "@/components/ui/github-install-prompt";
import { NextSeo } from 'next-seo';

import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { DEFAULT_API_SETTINGS } from '@/lib/api-config'

interface InstallationData {
  branchName: string;
  installationId: string;
  githubAppName: string;
  githubAppId: string;
}

export default function Home() {
  const [brandName, setBrandName] = useState("MyInstantAPI");
  const { data: session, status } = useSession();
  const [wasAuthenticated, setWasAuthenticated] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [installationData, setInstallationData] = useState<InstallationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  

  function getBrandingName(): string {
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (hostname.includes("quickstartapi.com")) return "QuickStartAPI";
    }
    return "MyInstantAPI";
  }
  
  // Session expiration effect - only check once after initial load
  useEffect(() => {
    // Skip this effect during loading state or when no session
    if (!session || status !== 'authenticated') return;
    
    // Get expiration time
    const expiration = new Date(session.expires).getTime();
    const now = Date.now();
    const timeout = expiration - now;
    
    // Only set up the timer if the expiration is reasonably far in the future
    // This prevents logout during token refresh or short lived sessions
    if (timeout > 60000) { // More than 1 minute left
      console.log(`Session will expire in ${Math.floor(timeout/60000)} minutes`);
      
      // Set the auto-logout timer
      const timer = setTimeout(() => {
        console.log("Session expired, logging out");
        signOut();
      }, timeout);
      
      // Clean up the timer when component unmounts
      return () => clearTimeout(timer);
    } else {
      console.log("Session expiration too close or invalid, not setting auto-logout");
    }
  }, []); // Empty dependency array - run only once after mount
  
  useEffect(() => {
    if (session) {
      window.gtag('event', 'page_view', {
        user_id: session.user.id,
        method: 'github'
      });
    }
  }, [session]);

  useEffect(() => {
    if (status === 'authenticated' && !wasAuthenticated) {
      window.gtag('event', 'user_login', {
        user_id: session?.user?.id,
        method: 'github'
      });
      setWasAuthenticated(true);
    }
  }, [status, wasAuthenticated, session]);  

  useEffect(() => {
    if (status === 'unauthenticated') {
      setWasAuthenticated(false);
      setIsSigningOut(false);
    }
  }, [status]);

  useEffect(() => {
    // Track when we transition from authenticated to loading (sign out process)
    if (wasAuthenticated && status === 'loading') {
      setIsSigningOut(true);
    }
    // Reset data when signing out completes
    if (isSigningOut && status === 'unauthenticated') {
      setInstallationData(null);
      setDataFetched(false);
      setError(null);
      setInstallUrl(null);
    }
  }, [status, wasAuthenticated, isSigningOut]);
  
  useEffect(() => {
    setBrandName(getBrandingName());
    const checkMobile = () => {
      // Change breakpoint to 700px to match your current width
      const isMobileView = window.innerWidth < 700;
      setIsMobile(isMobileView);
    }
    
    // Run on mount
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, [])

  useEffect(() => {
    if (status === 'authenticated' && session?.user?.id) {
      // Track successful session establishment
      window.gtag('event', 'session_started', {
        user_id: session.user.id, // Note: hash or anonymize if needed
        method: 'github'
      });
    }
  }, [status, session]);

  useEffect(() => {
    if (installationData && !installUrl) {
      // If we have installation data and no install URL, it means installation was successful
      window.gtag('event', 'github_app_install_complete', {
        installation_id: installationData.installationId,
        github_app_name: installationData.githubAppName
      });
    }
  }, [installationData, installUrl]);

  // Fetch installation data (GitHub app installation info)
  useEffect(() => {
    console.log("Checking session/dataFetched/isLoading:", session?.user?.email, dataFetched, isLoading);
    if (session?.user?.email && !dataFetched && !isLoading) {
      setIsLoading(true);
      setError(null);
      
      // Fetch installation data from the projects API (reusing existing endpoint)
      fetch('/api/projects')
        .then(async response => {
          const data = await response.json();
          
          if (!response.ok) {
            if (response.status === 401) {
              console.error('Not authenticated - signing out');
              signOut();
              return Promise.resolve(); // Return early without throwing
            }
            if (response.status === 403 && data.error === 'GitHub app not installed') {
              setInstallUrl(data.installUrl);
              window.gtag('event', 'github_app_install_needed', {
                user_id: session.user.id,
                error: 'GitHub app not installed'
              });
              throw new Error('GitHub app not installed');
            }
            if (data.error == "Invalid or expired GitHub token") {
              window.gtag('event', 'github_token_error', {
                user_id: session.user.id,
                error: 'Invalid or expired token'
              });
              console.error('Invalid or expired GitHub token - signing out');
              signOut();
              return Promise.resolve(); // Return early without throwing
            } else {
              console.error(data.error || 'Failed to fetch installation data');
              // Don't automatically sign out on general errors
            }
          }
          
          return data;
        })
        .then(data => {
          console.log("Fetched installation data:", data);
          
          // Extract installation data from the response
          if (data) {
            setInstallationData({
              branchName: data.branchName || 'main',
              installationId: data.installationId || '',
              githubAppName: data.githubAppName || 'myinstantapi',
              githubAppId: data.githubAppId || '1125065'
            });
            setInstallUrl(null);
            setDataFetched(true);
            window.gtag('event', 'installation_data_fetch_success', {
              user_id: session.user.id,
              installation_id: data.installationId
            });
          } else {
            setError("Failed to fetch installation data");
            setDataFetched(true);
          }
        })        
        .catch(err => {
          console.error("Error fetching installation data:", err);
          setError(err.message);
          setInstallationData(null);
          setDataFetched(true);
          // Don't automatically sign out on fetch errors
        })
        .finally(() => setIsLoading(false));
    }
  }, [session, dataFetched, isLoading]);

  useEffect(() => {
    if (!session) {
      setDataFetched(false);
    }
  }, [session]);

  if (status === "loading" && !isSigningOut) {
    return (
      <div className="px-8">
        <Card>
          <CardContent className="flex items-center justify-center py-20">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Loading...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-8 space-y-6">
      <NextSeo
        title={brandName}
        description="Host your API securely in seconds"
        openGraph={{
          title: 'Host your API securely in seconds',
          description: 'Host your API securely in seconds',
          url: 'https://www.myinstantapi.com',
          images: [
            {
              url: 'https://www.myinstantapi.com/myInstantAPI.png',
              width: 800,
              height: 600,
              alt: 'Og Image Alt',
              type: 'image/png',
            } 
          ],
        }}
      />
      {session && status === 'authenticated' && (
        <div className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-lg text-muted-foreground">Loading your APIs...</p>
            </div>
          ) : installUrl ? (
            <GitHubInstallPrompt installUrl={installUrl} />
          ) : error ? (
            <div className="p-4 rounded-md bg-red-50 border border-red-200">
              <p className="text-red-700">{error}</p>
            </div>
          ) : installationData ? (
            <DeployedProjectTable 
              username={session.user?.email || ''}
              githubHandle={session.user?.githubHandle || ''}
              defaultSettings={DEFAULT_API_SETTINGS}
              branchName={installationData.branchName}
              installationId={installationData.installationId}
              githubAppName={installationData.githubAppName}
              githubAppId={installationData.githubAppId}
            />
          ) : null}
        </div>
      )}
      
      {status === 'unauthenticated' && !isMobile && (<CodeBox />)}
      {status === 'unauthenticated' && isMobile && (
        <div className="min-h-[80vh] flex flex-col">
          <div className="flex-grow flex flex-col items-center ">
            <div className="text-center mb-8">
                <h1 className="font-sans font-black text-[6.95rem] md:text-6xl lg:text-8xl leading-[1.1] tracking-tight text-foreground">Host your<br />
                API now
                </h1>
            </div>

            <Button 
              onClick={() => signIn('github')}
              className="w-full max-w-xs"
              variant="outline"
              style={{backgroundColor:"#2563eb", color:"white"}}
            >
              Sign in with GitHub
            </Button>
          </div>
        </div>
      )}
      <Toaster />
    </div>
  );
}